import { useEffect } from 'react'
import { Link, Outlet, useNavigate } from 'react-router'
import type { JSX, ReactNode } from 'react'
import { api, call } from '@/api/client'
import { useMutation } from '@/api/query'
import { DEFAULT_HOME } from '@/auth/routes'
import { ThemeToggle } from '@/components/ThemeToggle'
import { Lockup } from '@/shell/Lockup'
import { Button } from '@/ui/Button'
import { ErrorState, FullPageLoading, InlineError } from '@/ui/states'
import { useToast } from '@/ui/toast-context'
import { useOnboarding, useWizard } from './context'
import { isNotConfigured, meterPercent, metaFor, pathFor, positionOf, timeRemaining } from './steps'
import type { StepKey } from './steps'

/**
 * The frame every wizard screen sits in — S-D2's chrome, without the review rail.
 *
 * Its own layout rather than `AppShell`, because a tenant part-way through setup
 * has nothing for a sidebar to describe: no keywords to link to, no dashboard
 * worth showing, and a module switcher between two empty modules. S-D2 draws it
 * as a single centred column and that is what this is.
 *
 * The step rail is **navigable**. Every dot is a link, including to steps that
 * are already done, because S-D2's *Back* button exists to change an answer —
 * and a rail that only moved forward would make the answer already given
 * unreachable without starting again.
 */
export function WizardLayout(): JSX.Element {
  const { status, failure, reload } = useOnboarding()

  if (status === 'loading') return <FullPageLoading label="Loading your setup" />
  if (status === 'error' && failure) {
    return (
      <div className="app-full-state">
        <ErrorState failure={failure} title="Could not load your setup" onRetry={reload} />
      </div>
    )
  }

  return (
    <div className="app-wizard">
      <div className="app-wizard__bar">
        {/* The lockup is the way back to the product (parity P-ONBR-02): the
            resume screen's footer lost its *Back to the dashboard* button to
            match the drawing, and the dashboard is usable with steps
            outstanding, so the mark leads there rather than to the wizard's
            own index. */}
        <Link className="app-public__lockup" to={DEFAULT_HOME}>
          <Lockup />
        </Link>
        <span className="app-spacer" />
        <ThemeToggle />
      </div>
      <Outlet />
    </div>
  )
}

/**
 * The progress bar and the step rail, above every step's card.
 *
 * The **order** is still the server's — see `onboarding/steps.ts` for why the SPA
 * holds no second one. What is counted here is the tenant's *position* in it:
 * `meterPercent` and the "Step N of 6" beside it are two renderings of the same
 * number, and `steps.ts` argues why that is not `progress.percent`.
 *
 * **Both halves are optional, because S-D2 drops them (ONB-32/33).** The two
 * failure screens keep the rail and lose the meter — a bar that says how far
 * along you are is the wrong thing to draw above a step that just went wrong —
 * screen 5∅ has neither, and screen 6 keeps the meter and drops the rail. They
 * are separate props rather than one "chrome" enum because those are four
 * genuinely different combinations and an enum would name them all badly.
 */
export function WizardChrome({
  current,
  meter = true,
  rail = true,
}: {
  current: StepKey
  meter?: boolean
  rail?: boolean
}): JSX.Element {
  const state = useWizard()
  const position = positionOf(state, current)

  return (
    <>
      {meter ? (
        <div className="app-wiz__progress">
          <div className="app-wiz__progress-label">
            <span>
              Step {position} of {state.steps.length}
            </span>
            <span>{timeRemaining(state, current)}</span>
          </div>
          <div className="ar-meter">
            <div
              className="ar-meter__fill"
              style={{ width: `${String(meterPercent(state, current))}%` }}
            />
          </div>
        </div>
      ) : null}

      {rail ? (
        <ol className="app-steps">
          {state.steps.map((row, index) => {
            const meta = metaFor(row.step)
            if (!meta) return null
            const isCurrent = row.step === current
            return (
              <li key={row.step}>
                <Link
                  className="app-step"
                  to={pathFor(row.step)}
                  data-state={
                    // A step this deployment cannot run is not a failure of the
                    // tenant's — see `isNotConfigured`. It is drawn as untouched.
                    row.status === 'failed' && isNotConfigured(row) ? 'pending' : row.status
                  }
                  aria-current={isCurrent ? 'step' : undefined}
                >
                  {/* Each row's own position — `position` above belongs to the
                   *current* step, and using it here numbered every dot 1. */}
                  <span className="app-step__dot" aria-hidden="true">
                    {row.status === 'done'
                      ? '✓'
                      : row.status === 'failed' && !isNotConfigured(row)
                        ? '!'
                        : index + 1}
                  </span>
                  {meta.short}
                  <span className="ar-visually-hidden">
                    {isCurrent ? ' (current step)' : ` (${row.status})`}
                  </span>
                </Link>
              </li>
            )
          })}
        </ol>
      ) : null}
    </>
  )
}

/**
 * The footer every step shares: Back, Skip, and the step's own primary action.
 *
 * *Skip* is absent for step 1, which the server refuses to skip anyway — the
 * button is hidden because offering an action that 409s is worse than not
 * offering it, and `optional` comes from the server so the two cannot disagree.
 *
 * **`offerSkip` is the screen's half of that question, and it is a different
 * question (ONB-15/19).** The server says whether skipping is *permitted*; a
 * screen says whether it is *offered*, and S-D2 draws steps 5 and 6 with Back
 * and their primary action alone. Both are optional server-side and both settle
 * in one press of a button already on screen, so a third control that settles
 * them differently is a choice nobody needs offered — where on the keyword
 * screen's empty state, where the primary is hidden because there is nothing to
 * save, Skip is the only way forward and stays.
 *
 * The two are `&&`-ed rather than the prop overriding: a screen cannot offer a
 * skip the server would refuse, which is the invariant the original comment is
 * about.
 */
export function WizardFooter({
  current,
  offerSkip = true,
  skipTone = 'quiet',
  children,
}: {
  current: StepKey
  offerSkip?: boolean
  /**
   * `primary` when Skip is the only way forward — a step whose connector this
   * deployment cannot run (`SenderStep` on `not_configured`). Quiet by default
   * because everywhere else Skip is the alternative to the step's own action,
   * and two primary buttons in one footer is no guidance at all.
   */
  skipTone?: 'quiet' | 'primary'
  children: ReactNode
}): JSX.Element {
  const state = useWizard()
  const { adopt } = useOnboarding()
  const navigate = useNavigate()
  const toast = useToast()

  const index = state.steps.findIndex((row) => row.step === current)
  const previous = index > 0 ? state.steps[index - 1] : undefined
  const optional = state.steps[index]?.optional ?? true

  const skip = useMutation((step: string) =>
    call(() => api.POST('/api/v1/onboarding/steps/{step}/skip', { params: { path: { step } } })),
  )
  const back = useMutation((step: string) =>
    call(() => api.POST('/api/v1/onboarding/navigate', { body: { step } })),
  )

  // WEB-4. Both `onSkip` and `onBack` returned on `!result.ok` and rendered the
  // failure nowhere, so *Skip for now* looked like a button that does nothing —
  // which is exactly what a blocked step (`ONBOARDING_STEP_BLOCKED`) or a 5xx
  // produced. The failure from either is the same kind of thing to the person
  // reading it: the wizard did not move, and here is why.
  const skipReset = skip.reset
  const backReset = back.reset
  useEffect(() => {
    skipReset()
    backReset()
  }, [current, skipReset, backReset])

  /**
   * Go back, and say so when only half of it worked.
   *
   * WEB-10. Going back still happens whatever the server says, because the step
   * rail above navigates between steps with no call at all — a *Back* button
   * that refuses where the rail's own dot succeeds is the wizard looking broken,
   * and *Back* is not a destructive action to guard.
   *
   * What the call buys is the second sentence of the comment below, and that is
   * what is now reported when it does not land. The toast rather than
   * `InlineError`: navigating runs the reset effect above, which would clear
   * `back.state.failure` in the same commit that shows it. `ToastProvider` is
   * outermost precisely so a message raised on the way out of a screen survives
   * (see `App.tsx`).
   */
  async function onBack(): Promise<void> {
    if (!previous) return
    // The cursor moves server-side too, so closing the tab on the previous step
    // resumes there rather than on the step that was left behind.
    const result = await back.run(previous.step)
    if (result.ok) {
      adopt(result.data)
    } else {
      // `short` rather than `title`: the titles are questions ("Which searches
      // should we track?"), which do not read as the name of a place.
      const goingTo = metaFor(previous.step)?.short ?? 'the previous step'
      const leaving = metaFor(current)?.short ?? 'this step'
      toast.show({
        kind: 'warning',
        title: 'Your place was not saved',
        body: `${result.failure.message} You are back on ${goingTo}, but closing this tab will resume you on ${leaving}.`,
      })
    }
    void navigate(pathFor(previous.step))
  }

  /**
   * Skip, then follow the cursor — **unless it did not move.**
   *
   * The same dead-button `KeywordsStep.onContinue` documents: `_settle` leaves
   * the cursor where it is when nothing is outstanding, so skipping the last
   * unsettled step navigates to the page you are on and looks like a control
   * that does nothing.
   *
   * `/onboarding/done` is total here rather than a guess. The cursor stays only
   * when `next_step` finds nothing outstanding; `site` is the sole required step
   * and is the one step that cannot be skipped, so an all-settled wizard has it
   * `done` — which is exactly what `may_complete` asks.
   */
  async function onSkip(): Promise<void> {
    const result = await skip.run(current)
    if (!result.ok) return
    adopt(result.data)
    const next = pathFor(result.data.current_step)
    void navigate(next === pathFor(current) ? '/onboarding/done' : next)
  }

  return (
    <>
      {/* Above the footer rather than inside it: `ar-card__footer` is a flex
          row, and a form-summary placed in it becomes a flex item competing
          with the buttons for width. */}
      <InlineError failure={skip.state.failure ?? back.state.failure} />
      <div className="ar-card__footer">
        {previous ? (
          <Button
            busy={back.pending}
            busyLabel="Going back"
            onClick={() => {
              void onBack()
            }}
          >
            Back
          </Button>
        ) : null}
        <span className="ar-card__spacer" />
        {optional && offerSkip ? (
          <Button
            tone={skipTone}
            busy={skip.pending}
            busyLabel="Skipping"
            onClick={() => {
              void onSkip()
            }}
          >
            {/* ONB-3/9/12. The step's own words for what is being given up —
                "Skip — I'll connect later", "Skip — SEO only", "Skip" — rather
                than one "Skip for now" that names nothing. `metaFor` can miss
                on a step this build has never heard of, and the generic label
                is the right fallback there. */}
            {metaFor(current)?.skipLabel ?? 'Skip for now'}
          </Button>
        ) : null}
        {children}
      </div>
    </>
  )
}
